import React, { useEffect, useMemo, useState } from "react";
import ResetButton from "@/components/ResetButton";
import useDraft from "@/lib/useDraft";
import { Plus, AtSign, Bookmark, Library } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ModelPicker from "@/components/ModelPicker";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton, { useCost } from "@/components/GenerateButton";
import PromptLibrary from "@/components/PromptLibrary";
import LibraryPicker from "@/components/LibraryPicker";
import { Thumb } from "@/components/UploadZone";
import PromptBox, { renumberTags } from "@/components/PromptBox";
import { useAttachments } from "@/lib/useUploader";
import { api, mediaUrl } from "@/lib/api";

const ASPECTS = ["Auto", "1:1", "16:9", "9:16", "4:3", "3:4", "5:4", "9:8", "4:5"];
const QUALITIES = ["Low", "Medium", "High"];
const RESOLUTIONS = ["512", "1K", "2K"];
const COUNTS = [1, 2, 4];

export const IMAGE_GROUPS = [
  { label: "Featured", filter: (m) => m.featured && m.section === "image" },
  { label: "Image Models", filter: (m) => m.section === "image" },
];

const Pop = ({ label, value, testid, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="ax-chip" data-testid={`${testid}-trigger`}>
        <span className="text-zinc-500">{label}</span><span className="text-white font-medium">{value}</span>
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" align="start" className="w-auto p-1.5 bg-[#121214] border-zinc-800 rounded-xl" data-testid={`${testid}-menu`}>{children}</PopoverContent>
  </Popover>
);

const OptGrid = ({ options, value, onChange, enabled, testid, cols = 3 }) => (
  <div className={`grid gap-1`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
    {options.map((o) => {
      const ok = enabled(o);
      return (
        <button key={o} disabled={!ok} onClick={() => onChange(o)} title={ok ? "" : "Not supported by this model"}
          data-testid={`${testid}-${String(o).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          className={`px-3 h-8 rounded-md text-xs font-medium ${value === o ? "bg-zinc-100 text-black" : ok ? "text-zinc-300 hover:bg-zinc-800" : "text-zinc-700 cursor-not-allowed"}`}>{o}</button>
      );
    })}
  </div>
);

export default function ImageStudio({ projectId, models, configured, items, references, onUploaded, generate, onOpen, onRetry, seed , header, onChanged, modelPick, onReset}) {
  const imageModels = useMemo(() => models.filter((m) => m.section === "image"), [models]);
  const [model, setModel] = useState(null);
  const [prompt, setPrompt] = useDraft(`ax_draft_${projectId}_image`);
  const [aspect, setAspect] = useState("Auto");
  const [quality, setQuality] = useState("Medium");
  const [resolution, setResolution] = useState("1K");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const att = useAttachments(projectId, 8, onUploaded, true, "image-att");
  const fileRef = React.useRef(null);
  const barRef = React.useRef(null);
  const [barH, setBarH] = useState(220);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => { const h = el.offsetHeight; requestAnimationFrame(() => setBarH((o) => (Math.abs(o - h) > 1 ? h : o))); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!model && imageModels.length) setModel(imageModels.find((m) => m.available && m.featured) || imageModels.find((m) => m.available) || imageModels[0]);
  }, [imageModels, model]);
  useEffect(() => {
    const m = modelPick && imageModels.find((x) => x.id === modelPick.id);
    if (m) setModel(m);
  }, [modelPick, imageModels]);

  const caps = model?.capabilities || {};
  const lc = (a) => (a || []).map((x) => String(x).toLowerCase());
  const okAspect = (a) => lc(caps.aspect_ratios).includes(a.toLowerCase());
  const okQuality = (q) => lc(caps.qualities).includes(q.toLowerCase());
  const okRes = (r) => (caps.resolutions || []).includes(r);
  const okCount = (n) => n <= (caps.max_images || 1);
  const canAttach = !!caps.image_edit;

  useEffect(() => {
    if (!model) return;
    if (!okAspect(aspect)) setAspect(okAspect("Auto") ? "Auto" : (caps.aspect_ratios?.find((a) => ASPECTS.map((x) => x.toLowerCase()).includes(a)) || "1:1").replace("auto", "Auto"));
    if (!okCount(count)) setCount(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    if (!seed) return;
    setPrompt(seed.prompt || "");
    const s = seed.settings || {};
    if (s.aspect_ratio) setAspect(s.aspect_ratio === "auto" ? "Auto" : s.aspect_ratio);
    if (s.quality) setQuality(s.quality[0].toUpperCase() + s.quality.slice(1));
    if (s.resolution) setResolution(s.resolution);
    if (s.num_images) setCount(s.num_images);
    const m = imageModels.find((x) => x.id === seed.model_id);
    if (m) setModel(m);
    att.setItems((seed.image_ids || []).map((id) => references.find((r) => r.id === id)).filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const settings = {
    ...(okAspect(aspect) && { aspect_ratio: aspect.toLowerCase() }),
    ...(okQuality(quality) && { quality: quality.toLowerCase() }),
    ...(okRes(resolution) && { resolution }),
    num_images: okCount(count) ? count : 1,
  };
  const usd = useCost(model?.available && model.id, settings);

  const reason = !configured ? "Coming Soon" : !model?.available ? "This model isn't available yet"
    : att.uploading ? "Waiting for upload…" : !prompt.trim() ? "Write a prompt first"
    : prompt.length > (model?.max_prompt || 1e9) ? `Prompt too long — max ${model.max_prompt} characters` : "";

  const go = async () => {
    setBusy(true);
    await generate({ section: "image", mode: "create", model_id: model.id, prompt: prompt.replace(/@image_?(\d+)\b/g, "@image_$1"), settings, image_ids: canAttach ? att.ids : [] });
    setBusy(false);
  };

  const promptTags = att.items.map((r, i) => ({ tag: `image${i + 1}`, ref: r }));
  const tagWarn = !canAttach && att.items.length ? `${model?.name} is text-only — attached images and their @tags will be ignored` : "";

  const onKey = (e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !reason) go(); };

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      <div className="flex-1 overflow-y-auto px-6 pt-6" style={{ paddingBottom: barH + 16 }} data-testid="image-canvas">
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged}
          emptyTitle="Your images will appear here"
          emptyHint="Describe what you want in the prompt bar below, pick a model, and press Generate. Attach images with + to edit or guide the result." />
      </div>

      <div ref={barRef} className="absolute bottom-0 left-0 right-[10px] px-6 pb-5 pointer-events-none [&>*]:pointer-events-auto">
        <div className="ax-prompt-bar max-w-5xl mx-auto" data-testid="image-prompt-bar"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); canAttach && att.add(e.dataTransfer.files); }}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
            if (!files.length) return;
            e.preventDefault();
            if (!canAttach) { toast.info(`${model?.name} only works from text`); return; }
            att.add(files);
            toast.success(`Pasted ${files.length} image${files.length > 1 ? "s" : ""} — uploading`);
          }}>
          {att.items.length > 0 && (
            <div className="flex gap-2 flex-wrap" data-testid="image-attachments">
              {att.items.map((a, i) => <Thumb key={a.id || a.preview} a={a} size="h-12 w-12" testid={`image-attach-${i}`} onRemove={() => { att.remove(i); setPrompt((p) => renumberTags(p, i, "image", "")); }} />)}
              {!canAttach && <span className="text-[11px] text-amber-400 self-center" data-testid="attach-ignored">{model?.name} is text-only — attachments will be ignored</span>}
            </div>
          )}
          <PromptBox value={prompt} onChange={setPrompt} onKeyDown={onKey} testid="image-prompt" maxChars={model?.max_prompt} improve
            tags={promptTags}
            placeholder="Describe the image you want to create…  Paste or drop images here · Type @ to tag an attached image · Ctrl/⌘ + Enter to generate"
            className="bg-transparent text-[15px] placeholder:text-zinc-600" />
          {tagWarn && <div className="text-[11px] text-amber-300" data-testid="image-tag-warning">{tagWarn}</div>}
          <div className="flex items-center gap-1.5 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="image-attach-input"
              onChange={(e) => { att.add(e.target.files); e.target.value = ""; }} />
            <button className="ax-icon-chip" onClick={() => canAttach ? fileRef.current?.click() : toast.info(`${model?.name} only works from text`)}
              data-testid="image-attach-button" title={canAttach ? "Attach images (up to 8)" : "This model is text-only"}>
              <Plus className="w-4 h-4" />
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="ax-icon-chip" data-testid="image-reference-button" title="Use a previous upload"><AtSign className="w-4 h-4" /></button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-72 p-2 bg-[#121214] border-zinc-800 rounded-xl" data-testid="image-reference-menu">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-1 pb-2">Project uploads</div>
                {references.filter((r) => (r.mime || "").startsWith("image/")).length === 0
                  ? <p className="text-xs text-zinc-500 p-2">No uploads yet — attach an image with +.</p>
                  : <div className="grid grid-cols-4 gap-1.5 max-h-60 overflow-y-auto">
                    {references.filter((r) => (r.mime || "").startsWith("image/")).map((r) => (
                      <button key={r.id} onClick={() => att.addRef(r)} data-testid={`image-ref-${r.id}`} title={r.name}
                        className="aspect-square rounded-md overflow-hidden border border-zinc-800 hover:border-[#7AF25F]">
                        <img src={mediaUrl(r.url)} alt="" className="w-full h-full object-cover" />
                      </button>))}
                  </div>}
              </PopoverContent>
            </Popover>
            <button className="ax-chip !border-[#7AF25F]/50 !text-[#c9f7bd] hover:!bg-[#7AF25F]/10" data-testid="image-media-button" title="Attach from Media Library"
              onClick={() => canAttach ? setMediaOpen(true) : toast.info(`${model?.name} only works from text`)}>
              <Library className="w-4 h-4" />Attach from Media</button>
            <ResetButton onReset={onReset} testid="image-panel-reset" label="Reset" />
            <button className="ax-icon-chip" onClick={() => setLibOpen(true)} data-testid="image-library-button" title="Prompt library"><Bookmark className="w-4 h-4" /></button>
            <span className="w-px h-5 bg-zinc-800 mx-1" />
            <ModelPicker models={models} value={model?.id} onChange={setModel} configured={configured} groups={IMAGE_GROUPS} testid="image-model" />
            <Pop label="Ratio" value={okAspect(aspect) ? aspect : "—"} testid="image-aspect">
              <OptGrid options={ASPECTS} value={aspect} onChange={setAspect} enabled={okAspect} testid="image-aspect" />
            </Pop>
            <Pop label="Quality" value={okQuality(quality) ? quality : "—"} testid="image-quality">
              <OptGrid options={QUALITIES} value={quality} onChange={setQuality} enabled={okQuality} testid="image-quality" />
            </Pop>
            <Pop label="Res" value={okRes(resolution) ? resolution : "—"} testid="image-resolution">
              <OptGrid options={RESOLUTIONS} value={resolution} onChange={setResolution} enabled={okRes} testid="image-resolution" />
            </Pop>
            <Pop label="Count" value={settings.num_images} testid="image-count">
              <OptGrid options={COUNTS} value={count} onChange={setCount} enabled={okCount} testid="image-count" />
            </Pop>
            <div className="flex-1" />
            <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={reason} testid="image-generate" />
          </div>
          {reason && <div className="text-[11px] text-zinc-500 text-right -mt-1" data-testid="image-generate-hint">{reason}</div>}
        </div>
      </div>
      <LibraryPicker open={mediaOpen} onOpenChange={setMediaOpen} accept="image/png,image/jpeg,image/webp" max={Math.max(1, 8 - att.items.length)} onPick={(refs) => att.add(refs)} />
      <PromptLibrary open={libOpen} onOpenChange={setLibOpen} projectId={projectId}
        onApply={(p) => { setPrompt(p.prompt); setLibOpen(false); }} />
    </div>
  );
}
